import { inject, Injectable, OnDestroy, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Database, ref, onValue, DataSnapshot, query, limitToLast, push, set, get, update, orderByChild, equalTo } from '@angular/fire/database';
import { AuthService } from '../auth/auth.service';
import { BehaviorSubject, Observable, Subject, Subscription, from } from 'rxjs';

export interface RealtimeAlert {
  id?: string;
  description: string;
  title: string;
  url: string;
  query?: any;
  seen?: boolean;
  path?: string; // Internal property to track where the alert lives for marking as seen
}

@Injectable({
  providedIn: 'root'
})
export class FirebaseAlertService {
  private db = inject(Database);
  private auth = inject(AuthService);
  private injector = inject(EnvironmentInjector);

  private alertsSubject = new Subject<RealtimeAlert>();
  alerts$ = this.alertsSubject.asObservable();

  private userUnsubscribe?: () => void;
  private roleUnsubscribe?: () => void;
  private seenAlertIdsByPath = new Map<string, Set<string>>();

  constructor() {
    this.auth.user.subscribe(user => {
      if (user) {
// $#$        // /* alert(user.role); */
        this.setupListeners(user);
      } else {
        this.clearListeners();
      }
    });
  }

  private setupListeners(user: any) {
    this.clearListeners();

    const orgId = user.organizationId || user.hospitalId;
    const userId = user.fhirResourceId;
    let role = user.role;

    // Standardize pharmacy role mapping
    if (role === 'pharmacy') {
      role = 'pharmacist';
    }

    if (!orgId) return;

    // Use runInInjectionContext to safely call Firebase APIs in this async-triggered flow
    runInInjectionContext(this.injector, () => {
      // 1. Process User-specific Alerts
      if (userId) {
        const userPath = `organization/${orgId}/user/${userId}`;
        this.catchUpUnreadAlerts(userPath).then(() => {
          this.subscribeToNewAlerts(userPath, 'user');
        });
      }

      // 2. Process Role-specific Alerts
      if (role) {
        const rolePath = `organization/${orgId}/role/${role}`;
        this.catchUpUnreadAlerts(rolePath).then(() => {
          this.subscribeToNewAlerts(rolePath, 'role');
        });
      }
    });
  }

  /**
   * One-time fetch for all alerts that haven't been seen yet.
   * Fallback: We fetch all and filter in JS to avoid "Index not defined" errors.
   */
  private async catchUpUnreadAlerts(path: string) {
    try {
      // Wrap the get() call directly to preserve context after the async suspension
      const snapshot = await runInInjectionContext(this.injector, () => {
        const alertRef = ref(this.db, path);
        return get(alertRef);
      });

      if (snapshot.exists()) {
        const seenIds = this.getOrCreateSeenIds(path);
        snapshot.forEach((child) => {
          const data = child.val();
          const id = child.key as string;

          if (data && data.seen !== true && !seenIds.has(id)) {
            seenIds.add(id);
// $#$            // /* alert(id) */
            this.alertsSubject.next({ ...data, id, path });
          }
        });
      }
    } catch (error) {
      console.warn(`Catch-up fetch failed for ${path}.`, error);
    }
  }


  private subscribeToNewAlerts(path: string, type: 'user' | 'role') {
    // Use runInInjectionContext for the entire subscription logic
    runInInjectionContext(this.injector, () => {
      const alertRef = ref(this.db, path);
      const unsubscribe = onValue(query(alertRef, limitToLast(1)), (snapshot: DataSnapshot) => {
        const seenIds = this.getOrCreateSeenIds(path);
        const entries = this.extractSnapshotEntries(snapshot, path);

        entries.forEach((entry) => {
          if (!seenIds.has(entry.id) && entry.item.seen !== true) {
            seenIds.add(entry.id);
            this.alertsSubject.next(entry.item);
          }
        });
      });

      if (type === 'user') this.userUnsubscribe = unsubscribe;
      else this.roleUnsubscribe = unsubscribe;
    });
  }

  markAsSeen(path: string, alertId: string) {
    if (!path || !alertId) return;
    runInInjectionContext(this.injector, () => {
      const alertRef = ref(this.db, `${path}/${alertId}`);
      update(alertRef, { seen: true }).catch(err => {
        console.error('Failed to mark alert as seen:', err);
      });
    });
  }

  private getOrCreateSeenIds(path: string): Set<string> {
    if (!this.seenAlertIdsByPath.has(path)) {
      this.seenAlertIdsByPath.set(path, new Set<string>());
    }
    return this.seenAlertIdsByPath.get(path)!;
  }

  private extractSnapshotEntries(snapshot: DataSnapshot, path: string): Array<{ id: string; item: RealtimeAlert }> {
    if (!snapshot.exists()) return [];

    const entries: Array<{ id: string; item: RealtimeAlert }> = [];
    snapshot.forEach((child) => {
      const data = child.val();
      if (data && data.title && data.description) {
        entries.push({
          id: child.key!,
          item: { ...data, id: child.key!, path }
        });
      }
    });
    return entries;
  }

  private clearListeners() {
    if (this.userUnsubscribe) this.userUnsubscribe();
    if (this.roleUnsubscribe) this.roleUnsubscribe();
    this.userUnsubscribe = undefined;
    this.roleUnsubscribe = undefined;
    this.seenAlertIdsByPath.clear();
  }

  postRoleAlert(orgId: string, role: string, alert: RealtimeAlert): Observable<void> {
    if (role === 'pharmacy') {
      role = 'pharmacist';
    }
    const rolePath = `organization/${orgId}/role/${role}`;
    const roleRef = ref(this.db, rolePath);
    const payload = { ...alert, seen: false, timestamp: Date.now() };
    return from(push(roleRef, payload).then(() => { }));
  }

  postUserAlert(orgId: string, userId: string, alert: RealtimeAlert): Observable<void> {
    const userPath = `organization/${orgId}/user/${userId}`;
    const userRef = ref(this.db, userPath);
    const payload = { ...alert, seen: false, timestamp: Date.now() };
    return from(push(userRef, payload).then(() => { }));
  }
}
